import React from "react";
import { Card, Alert } from "react-bootstrap";

function CreatingPermissionGroups() {
  return (
    <div>
      <h2 className="mb-4">
        Creating Permission Groups
      </h2>

      <Alert variant="info">
        Learn how to create new permission groups to organize users and control access to runbooks.
      </Alert>

      <Card className="mb-4">
        <Card.Body>
          <h4>Step-by-Step: Creating a Group</h4>
          <ol>
            <li>Navigate to <strong>Permissions</strong> in the Settings section</li>
            <li>Click the <strong>Add Group</strong> button (top right corner)</li>
            <li>Fill in the group details:
              <ul className="mt-2">
                <li><strong>Group Name</strong> - A clear, descriptive name (e.g., "Finance Operators", "Developer Team")</li>
                <li><strong>Description</strong> - Explain the purpose of this group and who should be in it</li>
                <li><strong>Active</strong> - Check this box to make the group active immediately</li>
              </ul>
            </li>
            <li>Click <strong>Save</strong></li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Choosing a Group Name</h4>
          <p><strong>Good naming practices:</strong></p>
          <ul>
            <li><strong>Be Descriptive</strong> - "Finance Operators" is better than "Group 1"</li>
            <li><strong>Include Role or Department</strong> - Makes it clear who belongs</li>
            <li><strong>Indicate Access Level</strong> - "Read-Only Users", "Full Access Developers"</li>
            <li><strong>Keep it Short</strong> - Long names are harder to scan</li>
          </ul>
          <p className="mt-3"><strong>Examples of good group names:</strong></p>
          <ul>
            <li>"Finance Team - Operators"</li>
            <li>"HR Department - Read Only"</li>
            <li>"IT Developers"</li>
            <li>"Executive Dashboard Viewers"</li>
            <li>"Payroll Processors"</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Writing a Good Description</h4>
          <p>
            The description field helps you and other admins understand the group's purpose months later.
            Include:
          </p>
          <ul>
            <li><strong>Purpose</strong> - Why does this group exist?</li>
            <li><strong>Who</strong> - What type of users should be in it?</li>
            <li><strong>Access Level</strong> - What can they do?</li>
          </ul>
          <p className="mt-3"><strong>Example description:</strong></p>
          <p className="text-muted fst-italic">
            "Finance team members who execute monthly reporting runbooks. Can view and execute runbooks but cannot edit them.
            Has access to all finance-related automation workflows."
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Active vs Inactive Groups</h4>
          <ul>
            <li><strong>Active Groups</strong> - Members have access according to the group's permissions</li>
            <li><strong>Inactive Groups</strong> - Members temporarily lose access without removing them from the group</li>
          </ul>
          <p className="mt-3">
            <strong>Use Cases for Inactive Groups:</strong>
          </p>
          <ul>
            <li>Temporarily disable access for a team (e.g., contractors who are between projects)</li>
            <li>Test a new group configuration before activating it</li>
            <li>Preserve group setup while not currently needed</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>After Creating a Group</h4>
          <p>
            Once you've created a group, it exists but doesn't do anything yet. You need to:
          </p>
          <ol>
            <li><strong>Add Permissions</strong> - Define what actions group members can perform</li>
            <li><strong>Add Users</strong> - Add people to the group</li>
            <li><strong>Add Runbooks</strong> - Specify which runbooks the group can access</li>
          </ol>
          <Alert variant="info" className="mt-3">
            <strong>Tip:</strong> You can create the group first, then configure it step by step. Users won't have access until
            you've completed all three steps above.
          </Alert>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Editing a Permission Group</h4>
          <p>To edit an existing group:</p>
          <ol>
            <li>Go to the Permissions page</li>
            <li>Click on a group in the table to select it</li>
            <li>Click the <strong>Edit</strong> button</li>
            <li>Update the group name, description, or active status</li>
            <li>Click <strong>Save</strong></li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Deleting a Permission Group</h4>
          <ol>
            <li>Select the group from the table</li>
            <li>Click the <strong>Delete</strong> button</li>
            <li>Confirm the deletion</li>
          </ol>
          <Alert variant="warning" className="mt-3">
            <strong>Warning:</strong> Deleting a group removes all associated user memberships and runbook assignments.
            Users who only belonged to this group will lose access to runbooks. Consider making the group inactive instead of deleting it.
          </Alert>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Common Permission Group Patterns</h4>

          <h6 className="mt-3">By Department</h6>
          <p>Create groups for each department in your organization:</p>
          <ul>
            <li>"Finance Team"</li>
            <li>"HR Department"</li>
            <li>"IT Operations"</li>
            <li>"Sales Team"</li>
          </ul>

          <h6 className="mt-3">By Access Level</h6>
          <p>Create groups based on what users can do:</p>
          <ul>
            <li>"Read-Only Users"</li>
            <li>"Operators" (can execute, not edit)</li>
            <li>"Developers" (full access)</li>
            <li>"Viewers" (dashboard only)</li>
          </ul>

          <h6 className="mt-3">By Project or Function</h6>
          <p>Create groups for specific projects or business functions:</p>
          <ul>
            <li>"Month-End Close Team"</li>
            <li>"Payroll Processors"</li>
            <li>"Reporting Automation"</li>
            <li>"Data Integration Team"</li>
          </ul>
        </Card.Body>
      </Card>
    </div>
  );
}

export default CreatingPermissionGroups;
